import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useCallback, useEffect } from "react";
import { FormTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_time_masked_input";
import { useForm } from "react-hook-form";
import { CopyTreinamentoForm, CopyTreinamentoSchema } from "../../../schemas/Treinamentos_lista/copy_treinamento";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { Treinamento } from "../../../../domain/models/treinamento";

type Props = {
  open: boolean,
  saving: boolean,
  selectedTreinamento: Treinamento | null,
  onSubmit: (form: CopyTreinamentoForm) => void,
  onClose: (idCargo: number | null, openState: boolean) => void,
};

export const CopyTreinamentoModal = ({
  open,
  saving,
  selectedTreinamento,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<CopyTreinamentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(CopyTreinamentoSchema)
  });

  useEffect(() => {
    if (open) {
      if (selectedTreinamento) {
        hookForm.setValue('nome', selectedTreinamento.nome ?? '');
        hookForm.setValue('versao', selectedTreinamento.versao ?? '');
        hookForm.setValue('cargaHoraria', selectedTreinamento.cargaHoraria ?? 0);
      } else {
        hookForm.reset();
      }
    }
  }, [hookForm, open, selectedTreinamento]);

  const handleSubmit = useCallback((form: CopyTreinamentoForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);
  const handleClose = useCallback(() => {
    if (!saving) onClose(null, false);
  }, [
    saving,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Copiar treinamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormInput
                name='nome'
                label='Treinamento'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='versao'
                label='Versão'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm} />

              <FormTimeMaskedInput
                name='cargaHoraria'
                label='Carga Horária'
                readOnly={saving}
                reactHookForm={hookForm} />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  buttonColor="primary-green"
                  type="submit">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}